<template>
  <div class="about">

    <!-- Event Header Banner (Compact, Card Style) -->
    <div class="event-header-section">
      <div v-if="loadingEvent" class="event-header-card" style="display: flex; justify-content: center; align-items: center; min-height: 100px;">
        <font-awesome-icon icon="running" class="fa-3x running-animate" spin />
        <span style="margin-left: 1rem; font-size: 1.2rem; font-weight: 600;">{{$t('teamEventDashboard.loadingTeams')}}</span>
      </div>
      <div v-else class="event-header-card">
        <div class="event-header-content">
          <div class="event-basic-info">
            <div class="event-type-badge">
              <font-awesome-icon icon="swimmer" v-if="event.type==='Swim'" class="type-icon swim" />
              <font-awesome-icon icon="biking" v-if="event.type==='Ride'" class="type-icon bike" />
              <font-awesome-icon icon="running" v-if="event.type==='Run'" class="type-icon run" />
              <font-awesome-icon icon="walking" v-if="event.type==='Walk'" class="type-icon walk" />
              <span v-for="(type, idx) in event.type" :key="idx" class="type-icon-wrapper">
                <font-awesome-icon icon="swimmer" v-if="type=='Swim'" class="type-icon swim" />
                <font-awesome-icon icon="biking" v-if="type=='Ride'" class="type-icon bike" />
                <font-awesome-icon icon="running" v-if="type=='Run'" class="type-icon run" />
                <font-awesome-icon icon="walking" v-if="type=='Walk'" class="type-icon walk" />
              </span>
            </div>
            <div class="event-meta">
              <div class="event-status">
                <span v-if="isUpcomingEvent(event.from*1000)" class="status-badge upcoming">
                  <font-awesome-icon icon="clock" class="status-icon" />
                </span>
                <span v-if="isExpiredEvent(event.to*1000)" class="status-badge expired">
                  <font-awesome-icon icon="calendar-check" class="status-icon" />
                </span>
                <span v-if="!isUpcomingEvent(event.from*1000) && !isExpiredEvent(event.to*1000)" class="status-badge active">
                  <font-awesome-icon icon="play-circle" class="status-icon" />
                </span>
              </div>
            </div>
            <div class="event-name">{{ event.name }}</div>
            <div class="event-teams-count" v-if="paginator.count > 0">
              <font-awesome-icon icon="users" class="teams-count-icon" />
              <span class="teams-count-text">{{ $t('teamEventDashboard.teamsCount', {
                count: paginator.count,
                teamLabel: paginator.count === 1 ? $t('teamEventDashboard.team') : $t('teamEventDashboard.teams')
              }) }}</span>
            </div>
          </div>
          <div class="event-right-group">
            <div class="event-image">
              <img :src="event.image" :alt="event.name" class="event-thumbnail" />
            </div>
            <div class="event-actions">
              <router-link :to="{name: 'eventVar', params: {event_slug: event.slug}}" class="var-btn">
                <font-awesome-icon icon="chart-line" class="var-btn-icon" />
                <span class="var-btn-text">VAR</span>
              </router-link>
              <router-link :to="{name: 'eventMap', params: {slug: event.slug}}" class="map-btn">
                <font-awesome-icon :icon="['fas', 'map-marked-alt']" class="map-btn-icon" />
                <span class="map-btn-text">MAP</span>
              </router-link>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Card-based Team Grid -->
    <div class="team-grid">
      <div
        v-for="(t, idx) in teamStats"
        :key="t._id || idx"
        class="team-card"
        :class="{
          'gold': showRankings && idx === 0,
          'silver': showRankings && idx === 1,
          'bronze': showRankings && idx === 2
        }"
      >
        <div class="team-rank-badge" v-if="showRankings && idx < 3">
          <span v-if="idx === 0">🥇</span>
          <span v-else-if="idx === 1">🥈</span>
          <span v-else-if="idx === 2">🥉</span>
        </div>
        <router-link :to="`/event-dashboard/${event.slug}`" class="team-name">
          <div class="team-name-content">
            <span class="team-name-text">{{ t.name }}</span>
            <font-awesome-icon icon="arrow-right" class="team-name-arrow" />
          </div>
        </router-link>
        <div class="team-info">
          <span class="team-members">
            <font-awesome-icon icon="users" />
            {{$t('teamEventDashboard.members')}}: {{ t.total_members }}
          </span>
          <span class="team-points">
            <font-awesome-icon icon="trophy" />
            {{$t('teamEventDashboard.points')}}: <span class="points-badge">{{ t.total_point }}</span>
          </span>
        </div>
      </div>
    </div>

    <!-- Infinite Scroll Loading Indicator -->
    <div v-if="loadingMore && teamStats.length > 0" class="infinite-scroll-loading">
      <div class="loading-indicator">
        <font-awesome-icon icon="running" class="fa-3x running-animate" />
        <span class="loading-text">Loading more teams...</span>
      </div>
    </div>

    <!-- Empty State -->
    <div v-if="teamStats.length === 0 && !loadingMore" class="text-center py-5">
      <div class="empty-state">
        <div class="empty-icon-wrapper">
          <font-awesome-icon icon="users" class="empty-icon" />
        </div>
        <h3 class="empty-title">No teams found</h3>
        <p class="empty-description">No teams have joined this event yet.</p>
      </div>
    </div>

  </div>
</template>

<script>

import $backend from '../backend'

export default {
  name: 'TeamEventDashboard',
  metaInfo () {
    return {
      title: this.title
    }
  },
  data () {
    return {
      title: 'Team Event Dashboard',
      loadingEvent: true, // <-- add loading state for event
      // athletes: this.getAllActivities(),
      error: '',
      code: '',
      teamStats: [],
      event: {},
      athlete: {},
      athleteStats: {},
      isJoined: false,
      athleteNameSearchInput: '',
      filterByCond: {},
      bgColorAndGenderCss: { F: 'female-rank-bg', M: 'male-rank-bg' },
      tableBgByGender: '',
      paginator: {
        currentPage: 1,
        totalPages: 1,
        limit: 10, // row per page todo: make this configurable
        count: 0
      },
      loadingMore: false,
      allLoaded: false,
      activitiesFields: ['type', 'distance', 'avg_pace', 'avg_speed', { key: 'id', label: 'Soii/Report' }]
    }
  },
  created () {
    if (localStorage.hasOwnProperty('athlete')) {
      const athleteData = localStorage.getItem('athlete')
      if (athleteData && athleteData !== 'undefined') {
        try {
          this.athlete = JSON.parse(athleteData)
        } catch (e) {
          console.error('Error parsing athlete data:', e)
          localStorage.removeItem('athlete')
        }
      }
    }

    let slug = this.$route.params.slug
    this.loadingEvent = true // set loading true before fetching event
    this.getEvent(slug)
    this.title = this.title + ' | ' + slug
    this.loadTeamStats(slug)

    if (/^\?fbclid=/.test(location.search)) {
      location.replace(location.href.replace(/\?fbclid.+/, ''))
    }
  },
  mounted () {
    window.addEventListener('scroll', this.handleScroll)
  },
  beforeDestroy () {
    window.removeEventListener('scroll', this.handleScroll)
  },
  methods: {
    rowClass (item, type) {
      if (!item || type !== 'row') return
      if (item.kind_of_activity === 'brick') return 'table-success'
    },
    isUpcomingEvent: function (fromTs) {
      return fromTs > Date.now()
    },
    isExpiredEvent: function (toTs) {
      return toTs < Date.now()
    },
    gotoPage (page) {
      if (page < 1) {
        page = 1
      }
      if (page > this.paginator.totalPages) {
        page = this.paginator.totalPages
      }
      let limit = this.paginator.limit
      let offset = (page - 1) * limit
      let slug = this.$route.params.slug
      $backend.getEventAthlete(slug, limit, offset, this.filterByCond).then(responseData => {
        this.athletes = responseData.data.athletes
        this.paginator.count = responseData.data.count
        this.paginator.totalPages = Math.ceil(this.paginator.count / this.paginator.limit)
        this.paginator.currentPage = page
        if (this.paginator.currentPage > this.paginator.totalPages) {
          this.paginator.currentPage = this.paginator.totalPages
        }

        let athleteID = this.athlete._id
        let isJoin = this.isJoined

        this.athletes.forEach(function (item) {
          if (item.athlete.id === athleteID) {
            isJoin = true
          }
        })
        this.isJoined = isJoin
      }).catch(error => {
        // Check if it's an unauthorized error - axios interceptor will handle redirect
        if (error && error.data && error.data.msg === 'unauthorized') {
          return
        }
        this.error = error.data ? error.data.msg : 'An error occurred'
      })
    },

    loadTeamStats (slug, page = 1, append = false) {
      if (this.loadingMore || this.allLoaded) return
      this.loadingMore = true
      const limit = this.paginator.limit
      const offset = (page - 1) * limit
      $backend.getEventStatsByTeam(slug, limit, offset).then(responseData => {
        const newTeamStats = responseData.data.team_stats
        if (append) {
          this.teamStats = this.teamStats.concat(newTeamStats)
        } else {
          this.teamStats = newTeamStats
        }
        this.paginator.count = responseData.data.count
        this.paginator.totalPages = Math.ceil(this.paginator.count / this.paginator.limit)
        this.paginator.currentPage = page
        if (this.teamStats.length >= this.paginator.count) {
          this.allLoaded = true
        }
        this.loadingMore = false
      }).catch(error => {
        // Check if it's an unauthorized error - axios interceptor will handle redirect
        if (error && error.data && error.data.msg === 'unauthorized') {
          this.loadingMore = false
          return
        }
        this.error = error.data ? error.data.msg : 'An error occurred'
        this.loadingMore = false
      })
    },
    getEvent (slug) {
      $backend.getEvent(slug)
        .then(response => {
          this.event = response.data.event
          this.$emit('event-loaded', this.event)
          if (this.event.v2_rule === true) {
            this.activitiesFields = ['type', {
              key: 'kind_of_activity',
              'label': 'I/O'
            }, 'km', 'block', 'point', { key: 'id', label: 'Soii/Report' }]
          }
          this.loadingEvent = false // set loading false after event loaded
        }).catch(error => {
          // Check if it's an unauthorized error - axios interceptor will handle redirect
          if (error && error.data && error.data.msg === 'unauthorized') {
            this.loadingEvent = false
            return
          }
          this.error = error.data ? error.data.msg : 'An error occurred'
          this.loadingEvent = false // set loading false on error
        })
    },
    join: function (slug) {
      if (typeof this.athlete.token === 'undefined') {
        // Save current URL for redirect after successful login
        const currentUrl = this.$route.fullPath
        localStorage.setItem('redirectUrl', currentUrl)
        this.$router.push({ name: 'login' })
        return
      }
      $backend.join(this.athlete, slug, this.code)
        .then(response => {
          this.$router.push({ name: 'profile' })
        }).catch(error => {
          this.error = error.data.msg
          if (this.error === 'duplicated action') {
            this.error = '^.^'
          }
          if (this.error === 'code is required to join this event') {
            this.code = 'hãy nhập code /code is required to join this event'
          }
          if (this.error === 'code mismatch') {
            this.code = 'code sai /code mismatch'
          }
        })
    },
    filter: function (sex) {
      let limit = this.paginator.limit
      let offset = 0
      this.paginator.currentPage = 1
      let slug = this.$route.params.slug
      if (sex !== '') {
        this.filterByCond = { athlete_sex: sex }
        this.tableBgByGender = this.bgColorAndGenderCss[sex]
      } else {
        this.filterByCond = {}
        this.tableBgByGender = ''
      }
      $backend.getEventAthlete(slug, limit, offset, this.filterByCond).then(responseData => {
        this.athletes = responseData.data.athletes
        this.paginator.count = responseData.data.count
        this.paginator.totalPages = Math.ceil(this.paginator.count / this.paginator.limit)
        let athleteID = this.athlete._id
        let isJoin = this.isJoined

        this.athletes.forEach(function (item) {
          if (item.athlete.id === athleteID) {
            isJoin = true
          }
        })
        this.isJoined = isJoin
      }).catch(error => {
        this.error = error.data.msg
      })
    },
    searchByName (athleteNameSearchInput) {
      let limit = this.paginator.limit
      let offset = 0
      let slug = this.$route.params.slug
      $backend.searchAthleteInEvent(slug, athleteNameSearchInput, limit, offset).then(responseData => {
        this.athletes = responseData.data.athletes
        this.paginator.count = responseData.data.count
        this.paginator.totalPages = Math.ceil(this.paginator.count / this.paginator.limit)
      }).catch(error => {
        if (error.data.msg) {
          this.error = error.data.msg
        }
      })
    },

    stats (activities) {
      let dict = {}
      this.event.type.map((t) => {
        dict[t] = { blocks: 0, kms: 0, points: 0 }
      })

      activities.map((act) => {
        dict[act.type].blocks += act.block
        dict[act.type].kms += act.km
        dict[act.type].points += act.point
      })
      this.athleteStats = dict
    },

    resetAthleteStats () {
      this.athleteStats = {}
    },
    report (eventId, whoBeingReport, actLink) {
      alert('In sport we trust! Take it easy lah (^.^)')

      $backend.reportActivity(eventId, whoBeingReport, actLink, this.athlete).then(responseData => {
        alert(responseData.msg)
      }).catch(error => {
        if (error.data.msg) {
          alert(error.data.msg)
        }
      })
    },
    getCert (eventID, athleteID) {
      $backend.getCert(eventID, athleteID).then(resp => {
        window.open('/api/cert/' + eventID + '/' + athleteID, '_blank')
      }).catch(err => {
        console.log(err)
      })
    },
    inspectActivity (eventId, whoBeingInspected, actLink) {
      $backend.inspectActivity(eventId, whoBeingInspected, actLink, this.athlete).then(responseData => {
        console.log(responseData.msg)
        window.open(actLink, '_blank')
      }).catch(error => {
        if (error.data.msg) {
          console.log(error.data.msg)
          // if (error.data.msg == 'unauthorized') {
          //  alert('Vui lòng đăng nhập để Soy :P')
          //  this.$router.push('/login')
          // }
        }
        window.open(actLink, '_blank')
      })
    },
    loadMore () {
      if (this.loadingMore || this.allLoaded) return
      const nextPage = this.paginator.currentPage + 1
      this.loadTeamStats(this.$route.params.slug, nextPage, true)
    },

    handleScroll () {
      if (this.loadingMore || this.allLoaded) return
      const scrollY = window.scrollY || window.pageYOffset
      const visible = window.innerHeight
      const pageHeight = document.documentElement.scrollHeight
      if (scrollY + visible + 200 >= pageHeight) {
        this.loadMore()
      }
    }
  },
  computed: {
    now: function () {
      return new Date()
    },
    showRankings: function () {
      // Show rankings when at least one team has points > 0
      if (!this.teamStats || this.teamStats.length === 0) {
        return false
      }
      return this.teamStats.some(team => team.total_point > 0)
    }
  }
}

</script>

<style lang="scss">
ul {
  list-style-type: none;
  padding-left: 0;
}

#event-dashboard ul li {
  font-size: 10px;
}

.event-header-section {
  margin-bottom: 1.5rem;
}
.event-header-card {
  background: white;
  border-radius: 16px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  overflow: hidden;
  padding: 0.5rem 0.5rem !important;
  border-radius: 10px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.06);
}
.event-header-content {
  display: flex;
  align-items: center;
  padding: 0.75rem 1rem !important;
  gap: 0.8rem !important;
  text-align: left !important;
  flex-direction: row !important;
  justify-content: space-between;
}
.event-basic-info {
  flex: 1;
  display: flex !important;
  flex-direction: row !important;
  align-items: center !important;
  gap: 0.5rem !important;
  min-width: 0;
  margin-right: 0.5rem;
}
.event-type-badge {
  display: flex;
  gap: 0.2rem;
  font-size: 0.9rem;
  margin-right: 0.5rem;
  align-items: center;
}
.type-icon {
  font-size: 1.25rem;
  padding: 0.5rem;
  border-radius: 50%;
  background: rgba(74, 144, 226, 0.1);
  color: #4a90e2;
  transition: all 0.3s ease;
}
.type-icon.swim {
  color: #00b4d8;
  background: rgba(0, 180, 216, 0.1);
}
.type-icon.bike {
  color: #06ffa5;
  background: rgba(6, 255, 165, 0.1);
}
.type-icon.run {
  color: #ff6b35;
  background: rgba(255, 107, 53, 0.1);
}
.type-icon.walk {
  color: #7209b7;
  background: rgba(114, 9, 183, 0.1);
}
.event-name {
  font-size: 0.85rem;
  font-weight: 600;
  margin: 0 0.5rem 0 0;
  line-height: 1.1;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 90px;
  color: #2c3e50;
}

/* Hide event name on mobile devices */
@media (max-width: 768px) {
  .event-name {
    display: none !important;
  }

  .event-basic-info .event-name {
    display: none !important;
  }
}
.event-teams-count {
  display: flex;
  align-items: center;
  gap: 0.3rem;
  font-size: 0.75rem;
  color: #7f8c8d;
  margin-top: 0.2rem;
}
.teams-count-icon {
  font-size: 0.8rem;
  color: #4a90e2;
}
.teams-count-text {
  font-weight: 500;
}
.event-meta {
  display: flex;
  flex-direction: row !important;
  align-items: center !important;
  gap: 0.3rem !important;
  font-size: 0.75rem;
  margin: 0 0.5rem 0 0;
}
.event-status {
  display: flex;
  gap: 0.1rem;
  margin-left: 0.2em;
}
.status-badge {
  display: flex;
  align-items: center;
  gap: 0.25rem;
  padding: 0.2rem 0.5rem;
  border-radius: 20px;
  font-size: 0.65rem;
  font-weight: 600;
  color: white;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
}
.status-badge.upcoming {
  background: linear-gradient(135deg, #00b4d8 0%, #0077b6 100%);
}
.status-badge.expired {
  background: linear-gradient(135deg, #e74c3c 0%, #c0392b 100%);
}
.status-badge.active {
  background: linear-gradient(135deg, #2ecc71 0%, #27ae60 100%);
}
.status-icon {
  font-size: 0.8rem;
}
.event-right-group {
  display: flex;
  align-items: center;
  gap: 0.8rem;
  margin-left: auto;
  flex-shrink: 0;
}

.event-image {
  flex-shrink: 0;
  align-self: center !important;
}
.event-thumbnail {
  width: 38px;
  height: 38px;
  border-radius: 7px;
  border-width: 2px;
  object-fit: cover;
  border: 3px solid #e9ecef;
  transition: transform 0.3s ease;
}

/* Event Actions */
.event-actions {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex-shrink: 0;
}

.var-btn {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.6rem 1rem;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  text-decoration: none;
  border-radius: 0.6rem;
  font-weight: 600;
  font-size: 0.9rem;
  transition: all 0.3s ease;
  box-shadow: 0 2px 8px rgba(102, 126, 234, 0.3);
  border: none;
  cursor: pointer;
}

.var-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 16px rgba(102, 126, 234, 0.4);
  color: white;
  text-decoration: none;
}

.var-btn:active {
  transform: translateY(0) scale(0.98);
}

.var-btn-icon {
  font-size: 1rem;
}

.var-btn-text {
  font-weight: 700;
  letter-spacing: 0.5px;
}

.map-btn {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.6rem 1rem;
  background: linear-gradient(135deg, #ff6b35 0%, #f7931e 100%);
  color: white;
  text-decoration: none;
  border-radius: 0.6rem;
  font-weight: 600;
  font-size: 0.9rem;
  transition: all 0.3s ease;
  box-shadow: 0 2px 8px rgba(255, 107, 53, 0.3);
  border: none;
  cursor: pointer;
}

.map-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 16px rgba(255, 107, 53, 0.4);
  color: white;
  text-decoration: none;
}

.map-btn:active {
  transform: translateY(0) scale(0.98);
}

.map-btn-icon {
  font-size: 1rem;
}

.map-btn-text {
  font-weight: 700;
  letter-spacing: 0.5px;
}

/* Mobile responsive for action buttons */
@media (max-width: 768px) {
  .event-actions {
    justify-content: center;
    margin-top: 0.75rem;
    gap: 0.3rem;
  }

  .var-btn, .map-btn {
    padding: 0.35rem 0.6rem;
    font-size: 0.75rem;
    min-width: 60px;
    justify-content: center;
  }

  .var-btn-icon, .map-btn-icon {
    font-size: 0.8rem;
  }
}
.event-header-row {
  display: flex;
  align-items: center;
  gap: 1.5rem;
  margin-bottom: 1.5rem;
  background: #fff;
  border-radius: 1rem;
  box-shadow: 0 2px 12px rgba(0,0,0,0.08);
  padding: 1rem 1.5rem;
}
.event-banner-img {
  max-width: 120px;
  max-height: 80px;
  border-radius: 0.7rem;
  object-fit: cover;
  box-shadow: 0 1px 6px rgba(0,0,0,0.07);
}
.event-header-info {
  display: flex;
  flex-direction: column;
  justify-content: center;
  flex: 1;
}
.event-icons {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 0.3rem;
}
.event-title-row {
  display: flex;
  align-items: center;
  gap: 0.7rem;
}
.event-title {
  font-size: 1.3rem;
  font-weight: 600;
  color: #2c3e50;
}
.event-status {
  font-size: 1rem;
  font-weight: 500;
}
.event-banner {
  max-width: 38%;
  align-self: center;
}
.male-rank-bg {
  color: black !important;
  background-color: azure !important;
}
.female-rank-bg {
  color: black !important;
  background-color: beige !important;
}
.overal-rank-bg {
  color: black !important;
  background-color: lightgray !important;
}
.team-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(260px, 1fr));
  gap: 1.5rem;
  margin-top: 2rem;
}
@media (min-width: 769px) {
  .team-grid {
    grid-template-columns: repeat(2, 1fr);
  }
}
.team-card {
  background: #fff;
  border-radius: 1rem;
  box-shadow: 0 2px 12px rgba(0,0,0,0.08);
  padding: 1.5rem 1rem 1rem 1rem;
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  transition: transform 0.2s, box-shadow 0.2s;
  border: 2px solid transparent;
}
.team-card:hover {
  transform: translateY(-4px) scale(1.03);
  box-shadow: 0 6px 24px rgba(0,0,0,0.13);
}
.team-card.gold {
  border-color: gold;
}
.team-card.silver {
  border-color: silver;
}
.team-card.bronze {
  border-color: #cd7f32;
}
.team-rank-badge {
  position: absolute;
  top: -1.2rem;
  left: 1rem;
  font-size: 2rem;
}
.team-name {
  font-size: 1.3rem;
  font-weight: 600;
  color: #2c3e50;
  margin-bottom: 0.5rem;
  text-align: center;
  text-decoration: none;
  display: block;
  width: 100%;
  transition: all 0.3s ease;
  cursor: pointer;
  -webkit-tap-highlight-color: rgba(74, 144, 226, 0.3);
}

.team-name:hover {
  transform: translateY(-2px);
  color: #4a90e2;
}

.team-name:active {
  transform: translateY(0) scale(0.98);
}

.team-name-content {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  padding: 0.8rem 1.2rem;
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
  border-radius: 0.8rem;
  border: 2px solid #e0e0e0;
  transition: all 0.3s ease;
  position: relative;
  overflow: hidden;
  min-height: 44px; /* Touch-friendly minimum height */
}

.team-name:hover .team-name-content {
  background: linear-gradient(135deg, #e3f2fd 0%, #bbdefb 100%);
  border-color: #4a90e2;
  box-shadow: 0 4px 12px rgba(74, 144, 226, 0.2);
}

.team-name:active .team-name-content {
  background: linear-gradient(135deg, #d1e7ff 0%, #a8d4ff 100%);
  transform: scale(0.98);
}

.team-name-text {
  font-weight: 600;
  color: inherit;
  user-select: none;
}

.team-name-arrow {
  font-size: 0.9rem;
  opacity: 0.8;
  transition: all 0.3s ease;
  transform: translateX(0);
  color: #4a90e2;
}

.team-name:hover .team-name-arrow {
  opacity: 1;
  transform: translateX(3px);
  color: #4a90e2;
}

/* Mobile-specific improvements */
@media (max-width: 768px) {
  .team-name-content {
    padding: 1rem 1.5rem;
    min-height: 48px;
    background: linear-gradient(135deg, #ffffff 0%, #f5f5f5 100%);
    border: 2px solid #4a90e2;
    box-shadow: 0 2px 8px rgba(74, 144, 226, 0.15);
  }

  .team-name-arrow {
    opacity: 1;
    font-size: 1rem;
    margin-left: 0.3rem;
  }

  .team-name-text {
    font-size: 1.1rem;
  }

  /* Add a subtle pulse animation for mobile to draw attention */
  .team-name-content::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(135deg, rgba(74, 144, 226, 0.1) 0%, rgba(74, 144, 226, 0.05) 100%);
    border-radius: 0.8rem;
    opacity: 0;
    animation: pulse-attention 2s infinite;
  }
}

@keyframes pulse-attention {
  0%, 100% {
    opacity: 0;
  }
  50% {
    opacity: 1;
  }
}
.team-info {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  align-items: center;
}
.team-members, .team-points {
  font-size: 1.1rem;
  display: flex;
  align-items: center;
  gap: 0.4em;
}
.points-badge {
  background: #f7d774;
  color: #222;
  border-radius: 0.7em;
  padding: 0.1em 0.7em;
  font-weight: bold;
  margin-left: 0.3em;
}

// Infinite Scroll Loading Indicator
.infinite-scroll-loading {
  margin-top: 2rem;
  padding: 2rem;
  text-align: center;
}

.loading-indicator {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1rem;
  padding: 2rem;
  background: white;
  border-radius: 1rem;
  box-shadow: 0 2px 12px rgba(0,0,0,0.08);
}

.loading-spinner-icon {
  font-size: 2rem;
  color: #4a90e2;
  animation: spin 1s linear infinite;
}

.loading-text {
  font-size: 1rem;
  color: #7f8c8d;
  font-weight: 500;
}

// Empty State
.empty-state {
  padding: 3rem 2rem;
  background: white;
  border-radius: 1rem;
  box-shadow: 0 2px 12px rgba(0,0,0,0.08);
  max-width: 400px;
  margin: 0 auto;
}

.empty-icon-wrapper {
  margin-bottom: 1.5rem;
}

.empty-icon {
  font-size: 4rem;
  color: #bdc3c7;
}

.empty-title {
  font-size: 1.5rem;
  font-weight: 600;
  color: #2c3e50;
  margin-bottom: 0.5rem;
}

.empty-description {
  font-size: 1rem;
  color: #7f8c8d;
  margin: 0;
}
.running-animate {
  animation: run-bounce 1s infinite linear;
}
@keyframes run-bounce {
  0% { transform: translateY(0); }
  20% { transform: translateY(-8px); }
  40% { transform: translateY(0); }
  100% { transform: translateY(0); }
}
</style>
